import { TZDate } from '@date-fns/tz'
import { format, formatDistance } from 'date-fns'

type DateTimeWithTimeZoneLabelOptions = {
  locale?: string | string[]
  timeZone?: string
}

type ZonedDateTimeWithLabel = {
  timeZoneName: string
  zonedDate: TZDate
}

const getReadableTimeZoneName = (
  dateTime: Date,
  timeZone: string,
  locale?: string | string[]
): string => {
  if (['UTC', 'Etc/UTC', 'Etc/GMT'].includes(timeZone)) return 'UTC'

  try {
    return (
      new Intl.DateTimeFormat(locale, {
        timeZone,
        timeZoneName: 'longGeneric',
      })
        .formatToParts(dateTime)
        .find(({ type }) => type === 'timeZoneName')?.value ??
      timeZone.replace(/_/g, ' ')
    )
  } catch {
    // A localized label is optional presentation data; retain the IANA name
    // when the runtime cannot produce a friendlier label.
    return timeZone.replace(/_/g, ' ')
  }
}

const getZonedDateTimeWithLabel = (
  dateTime: number | Date | string,
  options: DateTimeWithTimeZoneLabelOptions
): ZonedDateTimeWithLabel => {
  const date = new Date(dateTime)
  const timeZone =
    options.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone

  return {
    timeZoneName: getReadableTimeZoneName(date, timeZone, options.locale),
    zonedDate: new TZDate(date.getTime(), timeZone),
  }
}

const formatZonedDateTimeWithLabel = (
  dateTime: number | Date | string | undefined,
  options: DateTimeWithTimeZoneLabelOptions,
  includeDate: boolean
): string => {
  if (!dateTime) return ''

  try {
    const { timeZoneName, zonedDate } = getZonedDateTimeWithLabel(
      dateTime,
      options
    )
    const time = `${format(zonedDate, 'HH:mm:ss')} ${timeZoneName}`

    return includeDate ? `${time} | ${format(zonedDate, 'MM/dd/yyyy')}` : time
  } catch {
    // Match the existing formatter contract: invalid display values render
    // nothing and do not interrupt the surrounding page.
    return ''
  }
}

export function formatDateTime(
  dateTime?: number | Date | string,
  formatString = 'HH:mm:ss | MM/dd/yyyy'
): string {
  // formats dateTime to military time [hh]:[MM]:[ss], [mm]/[dd]/[yyyy]
  return format(dateTime ? new Date(dateTime) : new Date(), formatString)
}

export function formatDateTimeInTimeZone(
  dateTime?: number | Date | string,
  timeZone?: string,
  formatString = 'HH:mm:ss | MM/dd/yyyy'
): string {
  if (!dateTime || !timeZone) {
    return ''
  }

  try {
    return format(
      new TZDate(new Date(dateTime).getTime(), timeZone),
      formatString
    )
  } catch {
    return ''
  }
}

/**
 * Formats an instant in a requested timezone and includes a localized,
 * human-readable timezone name (for example, "Pacific Time"). The locale
 * and timezone default to the user's browser settings.
 */
export function formatDateTimeWithTimeZoneLabel(
  dateTime?: number | Date | string,
  options: DateTimeWithTimeZoneLabelOptions = {}
): string {
  return formatZonedDateTimeWithLabel(dateTime, options, true)
}

/** Formats a time with the same human-readable timezone used above. */
export function formatTimeWithTimeZoneLabel(
  dateTime?: number | Date | string,
  options: DateTimeWithTimeZoneLabelOptions = {}
): string {
  return formatZonedDateTimeWithLabel(dateTime, options, false)
}

export const formatDate = (dateTime: number | Date | string): string => {
  return dateTime ? format(new Date(dateTime), 'MM/dd/yyyy') : ''
}

export function formatDateTimeWithAt(
  dateTime?: number | Date | string
): string {
  const formatString = 'MM.dd.yyyy  @ HH:mm:ss'

  if (dateTime === undefined) {
    return format(new Date(), formatString)
  }
  try {
    return format(new Date(dateTime), formatString)
  } catch {
    return ''
  }
}

export function formatTimeDistance(dateTime: number | Date | string): string {
  return formatDistance(
    dateTime ? new Date(dateTime) : new Date(),
    new Date(),
    {
      addSuffix: true,
    }
  )
}

export function formatTimeForTableCell(
  dateTime: number | Date | string
): string {
  return `${formatDateTime(dateTime)} • ${formatTimeDistance(dateTime)}`
}

// Reference https://stackoverflow.com/questions/48172772/time-zone-issue-involving-date-fns-format
export function formatDateWithTzOffset(
  dateTime?: number | Date | string,
  formatString = 'MM.dd.yyyy'
): string {
  const dt = new Date(dateTime)
  const dateWithOffset = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000
  )
  return format(
    dateWithOffset ? new Date(dateWithOffset) : new Date(),
    formatString
  )
}

// Formats the date's own local wall-clock components directly, with no timezone
// conversion at all -- the schedule is sent/stored/displayed as raw local digits
// (see ENG-30856); a timezone is applied exactly once, at evaluation time, using
// the incident's own facility.
export const getLocalTimeString = (date: Date): string =>
  format(date, "yyyy-MM-dd'T'HH:mm:ss")
